import { useMemo } from 'react';
import type { AllowedBoundingBox, BoundingBox, GlobalGrid, PlanCell } from '../../../types/plan';
import { planCellKey } from '../../../utils/planCells';
import type { BulkPlacementPreview } from './types';

interface UseBulkPlacementPreviewParams {
  isBulkPlacementMode: boolean;
  bulkGridDimensions: { rows: number; cols: number } | null;
  bulkPlacementHoverPos: { row: number; col: number } | null;
  cellMap: Map<string, PlanCell>;
  allowedBoundingBox: AllowedBoundingBox;
  boundingBox: BoundingBox | null;
  globalGrid: GlobalGrid;
}

export const useBulkPlacementPreview = ({
  isBulkPlacementMode,
  bulkGridDimensions,
  bulkPlacementHoverPos,
  cellMap,
  allowedBoundingBox,
  boundingBox,
  globalGrid,
}: UseBulkPlacementPreviewParams) => {
  return useMemo<BulkPlacementPreview | null>(() => {
    if (!isBulkPlacementMode || !bulkGridDimensions || !bulkPlacementHoverPos) return null;

    const { rows, cols } = bulkGridDimensions;
    const { row: hoverRow, col: hoverCol } = bulkPlacementHoverPos;

    let minRow = hoverRow;
    let maxRow = hoverRow;
    let minCol = hoverCol;
    let maxCol = hoverCol;

    let rowCount = 1;
    while (rowCount < rows) {
      maxRow += 1;
      if (maxRow === 0) maxRow = 1;
      rowCount += 1;
    }

    let colCount = 1;
    while (colCount < cols) {
      maxCol += 1;
      if (maxCol === 0) maxCol = 1;
      colCount += 1;
    }

    const fitsInGrid = minRow >= allowedBoundingBox.minRow
      && maxRow <= allowedBoundingBox.maxRow
      && minCol >= allowedBoundingBox.minCol
      && maxCol <= allowedBoundingBox.maxCol;

    const occupiedCells: string[] = [];
    const previewCells: string[] = [];
    for (let row = minRow; row <= maxRow; row += 1) {
      if (row === 0) continue;
      for (let col = minCol; col <= maxCol; col += 1) {
        if (col === 0) continue;
        const key = planCellKey(row, col);
        previewCells.push(key);
        if (cellMap.has(key)) occupiedCells.push(key);
      }
    }

    const newBoundingBox = !boundingBox
      ? { minRow: 1, maxRow: rows, minCol: 1, maxCol: cols }
      : {
          minRow: Math.min(boundingBox.minRow, minRow),
          maxRow: Math.max(boundingBox.maxRow, maxRow),
          minCol: Math.min(boundingBox.minCol, minCol),
          maxCol: Math.max(boundingBox.maxCol, maxCol),
        };

    let resultingWidth = newBoundingBox.maxCol - newBoundingBox.minCol + 1;
    let resultingHeight = newBoundingBox.maxRow - newBoundingBox.minRow + 1;
    if (newBoundingBox.minRow < 0 && newBoundingBox.maxRow > 0) resultingHeight -= 1;
    if (newBoundingBox.minCol < 0 && newBoundingBox.maxCol > 0) resultingWidth -= 1;

    const maxBoundingBox = globalGrid.maxBoundingBox || 32;
    const fitsInBoundingBox = resultingWidth <= maxBoundingBox && resultingHeight <= maxBoundingBox;
    const isValid = fitsInGrid && occupiedCells.length === 0 && fitsInBoundingBox;

    return {
      minRow,
      maxRow,
      minCol,
      maxCol,
      rows,
      cols,
      previewCells,
      occupiedCells,
      isValid,
      fitsInGrid,
      fitsInBoundingBox,
      resultingWidth,
      resultingHeight,
    };
  }, [
    isBulkPlacementMode,
    bulkGridDimensions,
    bulkPlacementHoverPos,
    cellMap,
    allowedBoundingBox,
    boundingBox,
    globalGrid,
  ]);
};
